import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useMemo } from 'react';
import { ProjectEntryRow } from '../../components/ProjectEntrySection/RowContent/styled';
import { engineeringEntryActions, projectEntryActions } from '../../store';
import { Grid } from '@mantine/core';
import { handleFetchError } from '../../helpers';
import NewHourInput from '../../components/ProjectEntrySection/HoursInput/NewHourInput';
import FillWeekControl from '@/components/FillWeekControl';
import { SupportedSystems } from '@/shared';
import { errorSavingEntry, errorSavingEntryHours } from '../../shared/constants/toastrErrorMsg';
import { engineeringEnvironment } from '../../helpers/environmentHelpers';
import DeleteButton from '@/components/DeleteButton';
import dayjs from 'dayjs';


const EngineeringProjectRow = (props) => {
  const dispatch = useDispatch();

  const userId = useSelector(state => state.user.activeUser?.oid);
  const [project, setProject] = useState(props.project);
  const [entryData, setEntryData] = useState();

  useEffect(() => {
    setProject(props.project)
  }, [props.project])

  useEffect(() => {
    const newEntriesMap = new Map();
    const datesThisWeek = Object.keys(project.canEditByDay);
    for (const date of datesThisWeek) {
      const entry = project.entries.find(e => e.entryDate == date)

      newEntriesMap.set(date, entry ?? {});
    }

    setEntryData(newEntriesMap)
  }, [project.entries])

  const handleInputChange = async (change, finishedLoading, resetValueCallback) => {
    if (change.previousValue == change.newValue) {
      finishedLoading();
      return;
    }

    try {
      await dispatch(engineeringEntryActions.upsertEngineeringProjectTimeEntry(
        userId,
        change.date,
        change.newValue,
        project.projectNumber,
        project.projectId
      ));

      var updateCompletedMonth = dayjs(change.date).month() === dayjs(Array.from(entryData).date).month();
      await dispatch(
        projectEntryActions.updateCompletedWeeks(change.newValue - change.previousValue, updateCompletedMonth)
      );
    }
    catch (e) {
      handleFetchError(e, getErrMsg(e));
      resetValueCallback();
    }
    finally {
      finishedLoading();
    }
  }
  const getErrMsg = (e) => {
    // If error has a Message property (from ValidationErrorResponse), use it directly
    if (e && e.Message) {
      return { title: 'Error: Project Entries', message: e.Message };
    }
    if (e.Message === errorSavingEntryHours.message) {
      return errorSavingEntryHours;
    }
    return errorSavingEntry;
  }

  const canDelete = useMemo(() => {
    return props.project.entries.every(e => e.hours === 0);
  }, [props.project]);

  return (
    <ProjectEntryRow align='center' active="true" style={{ marginRight: '3px' }}>
      <Grid.Col lg={8} md={7} xs={12}>
        <Grid align="center">
          <Grid.Col span={12} lg={9}>
            <Grid>
              <Grid.Col span={3}>
                <a href={`${engineeringEnvironment()}/projects/${project.projectId}`} target="_blank" rel="noopener noreferrer">
                  {project.projectNumber}
                </a>
              </Grid.Col>
              <Grid.Col span={9}>{project.projectDescription}</Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={12} lg={3}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <FillWeekControl
                type={SupportedSystems.VPIN}
                projectData={{ projectId: project.projectId, projectNumber: project.projectNumber, canEditByDay: project.canEditByDay }}
              />
            </div>
          </Grid.Col>
        </Grid>
      </Grid.Col>
      <Grid.Col lg={4} md={5} sm={8} xs={12}>
        <Grid sm={{ justify: 'space-around' }} align="center">
          {entryData && Array.from(entryData).map(([date, entry]) => {
            return <NewHourInput key={`${date}-${entry.hours}`} id={date} entry={entry} editable={project.canEditByDay[date]} date={date} handleInputChange={handleInputChange} />
          })}
          {canDelete && (<DeleteButton projectId={project.projectId} handleDelete={props.handleDeleteProject} />)}
        </Grid>
      </Grid.Col>
    </ProjectEntryRow>
  )
}

EngineeringProjectRow.propTypes = {
  project: PropTypes.object,
  handleDeleteProject: PropTypes.func.isRequired,
}

export default EngineeringProjectRow;
